import React, { useMemo, useState } from "react";
import {
  Card,
  Col,
  Row,
  Form,
  Input,
  Button,
  Space,
  Switch,
  ColorPicker,
  Avatar,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";

import MainLayout from "components/MainLayout";

import useGet from "hooks/useGet";
import useUpdate from "hooks/useUpdate";
import useDelete from "hooks/useDelete";

import PageFailure from "components/PageFailure";
import { MaskedInput } from "antd-mask-input";
import useFormErrors from "hooks/useFormErrors";
import { name_regexp } from "helpers/regexp";
import ModalDelete from "components/ModalDelete";
import Alerts from "./components/Alerts";

const UserUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const path = `/users/${id}`;

  const [color, setColor] = useState(null);

  const bgColor = useMemo(() => color, [color]);

  const { is_loading, is_error, data, refetch } = useGet({
    path,
    query: ["users", id],
    onSuccess: (res) => setColor(res.data?.color),
  });

  const [form] = Form.useForm();

  const { is_saving, update } = useUpdate({
    path,
    form,
    onSuccess: refetch,
  });

  const { is_deleting, _delete } = useDelete({
    path,
    onSuccess: () => navigate("/users"),
  });

  const { pristine, has_error, onFieldsChange } = useFormErrors(data);

  if (is_error) {
    return (
      <PageFailure
        title="Nie znaleziono administratora."
        button_text="Wróć do listy"
        path="/users"
      />
    );
  }

  return (
    <MainLayout
      back_href="/users"
      breadcrumbs={[
        { name: "Administratorzy", path: "/users" },
        { name: data?.id || "Trwa pobieranie..." },
      ]}
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} md={24} lg={24} xl={8}>
          <Card title="Edytuj administratora" loading={is_loading}>
            <Form
              form={form}
              layout="vertical"
              onFieldsChange={onFieldsChange}
              initialValues={{
                first_name: data?.first_name,
                last_name: data?.last_name,
                email: data?.email,
                phone: data?.phone,
                is_active: data?.is_active,
                color: data?.color,
              }}
              onFinish={(values) => update(values)}
            >
              {(values) => (
                <>
                  <Form.Item
                    label="Imię"
                    name="first_name"
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: "Imię jest wymagane!",
                      },
                      {
                        pattern: name_regexp,
                        message: "Pole zawiera niedowolone znaki!",
                      },
                    ]}
                  >
                    <Input placeholder="Imię" maxLength={60} />
                  </Form.Item>
                  <Form.Item
                    label="Nazwisko"
                    name="last_name"
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: "Nazwisko jest wymagane!",
                      },
                      {
                        pattern: name_regexp,
                        message: "Pole zawiera niedowolone znaki!",
                      },
                    ]}
                  >
                    <Input placeholder="Nazwisko" maxLength={60} />
                  </Form.Item>
                  <Form.Item
                    label="E-mail"
                    name="email"
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        type: "email",
                        message: "E-mail jest niepoprawny!",
                      },
                    ]}
                  >
                    <Input placeholder="E-mail" maxLength={50} />
                  </Form.Item>
                  <Form.Item
                    required
                    hasFeedback
                    label="Telefon"
                    name="phone"
                    rules={[
                      () => ({
                        validator(_, value) {
                          if (!value) {
                            return Promise.reject(
                              new Error("Numer telefonu wymagany!")
                            );
                          }
                          if (value.includes("_")) {
                            return Promise.reject(
                              new Error("Numer telefonu niepoprawny!")
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <MaskedInput mask="000000000" placeholder="Telefon" />
                  </Form.Item>
                  <Form.Item valuePropName="checked" name="is_active">
                    <Switch
                      checkedChildren="Aktywny"
                      unCheckedChildren="Nieaktywny"
                      defaultChecked={data?.is_active}
                      onChange={(value) =>
                        form.setFieldValue("is_active", value)
                      }
                    />
                  </Form.Item>
                  <Form.Item name="color">
                    <ColorPicker
                      value={data?.color}
                      onChange={(value) => setColor(value.toHexString())}
                      onChangeComplete={(value) =>
                        form.setFieldValue("color", value.toHexString())
                      }
                    >
                      <Space>
                        <Avatar
                          shape="square"
                          style={{
                            cursor: "pointer",
                            backgroundColor: bgColor,
                          }}
                        >
                          {values.first_name?.charAt(0)?.toUpperCase()}
                          {values.last_name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Button
                          type="primary"
                          style={{ backgroundColor: bgColor }}
                        >
                          Zmień kolor
                        </Button>
                      </Space>
                    </ColorPicker>
                  </Form.Item>
                  <Row justify="end">
                    <Space>
                      <ModalDelete
                        loading={is_deleting}
                        text="administratora"
                        handleDelete={_delete}
                      />
                      <Button
                        disabled={pristine || has_error}
                        loading={is_deleting || is_loading || is_saving}
                        type="primary"
                        htmlType="submit"
                      >
                        Zapisz
                      </Button>
                    </Space>
                  </Row>
                </>
              )}
            </Form>
          </Card>
        </Col>
        <Col xs={24} md={24} lg={24} xl={16}>
          <Card title="Ostatnia atywność użytkownika" loading={is_loading}>
            <Alerts id={id} />
          </Card>
        </Col>
      </Row>
    </MainLayout>
  );
};

export default UserUpdate;
